<template>
	<div class="menu-container">
		<div class="menu-header pl-5 width-100 display-flex flex-row flex-align-center">Setup</div>
		<div class="content-and-footer-container display-flex flex-column flex-justify-space-between height-100">
			<Loader v-if="isSaving" />
			<div v-else class="menu-content height-100 display-flex flex-column pb-5">
				<SetupMenuContent
					:panels="panels"
					v-on="$listeners"
					ref="menu_content"
					:editMode="editMode"
					:openedPanelIndex="openedPanelIndex"
					:installationMode="installationMode"
					:configurationIds="configurationIds"
					:userConfInterface="userConfInterface"
					@tab-clicked="(i) => (openedPanelIndex = i)"
					:generalConfInterface="generalConfInterface"
					:generalConfigurationId="generalConfigurationId"
					:didUserTriedToSaveOnce="didUserTriedToSaveOnce"
					:notValidConfigurationsIds="unValidConfigurationsIds"
					:userSpecificConfigurationId="userSpecificConfigurationId"
				/>
			</div>
			<div class="footer-container flex-column flex-centerize width-100">
				<AnemicButton
					v-if="installationMode"
					class="installation-button"
					@click="onInstallationAdvance"
					:disabled="isInstallationButtonDisabled"
				>
					{{ installationButtonText }}
				</AnemicButton>
				<template v-else>
					<AppTooltip :disabled="!editMode" text="This button can only be clicked in test mode">
						<AnemicButton :disabled="editMode || isSaving" @click="saveConfigurations" class="save-button">Save</AnemicButton>
					</AppTooltip>
					<div v-if="showErrorMessage" class="error-message">Fix the following issues to save</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
import isPlainObject from 'lodash/isPlainObject';
import { mapActions, mapGetters } from 'vuex';

import { NONE_INTERFACE_CONFIGURATION_ONLY_FIELDS } from '@/components/BlockEditorWidget/widgetFieldTypes.const';
import AppTooltip from '@/components/common/AppTooltip';
import { setConfigurationsValues, setConfigurationValues } from '@/store/modules/configurationEditor/actions';
import { useConfigurationsValues } from '@/store/modules/configurationEditor/getters';
import { generalConfigurationId, userSpecificConfigurationId } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { saveConfigurationsResponses } from '@/store/modules/workflowConfigurations/actions';
import { getConfiguration } from '@/store/modules/workflowConfigurations/getters';
import DynamicDataService from '@/utils/dynamic-data-service';
import SetupMenuContent from '@/views/Flow/configurations/SetupMenu/SetupMenuContent';

export default {
	name: 'SetupMenu',
	components: {
		AppTooltip,
		SetupMenuContent,
	},
	props: {
		editMode: { type: Boolean },
		configurationIds: { type: Object },
		installationMode: { type: Boolean },
		applyConfigurationsResponseOnDraft: { type: Boolean },
	},
	computed: {
		...mapGetters([useConfigurationsValues.fullPath, getConfiguration.fullPath]),
		...mapGetters([userSpecificConfigurationId.fullPath, generalConfigurationId.fullPath]),
		installationButtonText() {
			if (this.isLastTab) {
				return 'Save and Activate';
			}
			return 'Next';
		},
		numOfTabs() {
			return this.panels.length;
		},
		isLastTab() {
			return this.openedPanelIndex + 1 === this.numOfTabs;
		},
		configurationsValues() {
			return this[useConfigurationsValues.fullPath] || {};
		},
		unValidConfigurationsIds() {
			let res = [];
			if (!this.isGeneralConfigurationValid && this.generalConfInterface) {
				res = [...res, this.generalConfId];
			}
			if (!this.isUserConfigurationValid && this.userConfInterface) {
				res = [...res, this.userConfId];
			}
			return res;
		},
		generalConfId() {
			return this.configurationIds?.generalConfigurationId;
		},
		generalConf() {
			return this[getConfiguration.fullPath](this.generalConfId);
		},
		generalConfInterface() {
			return this.getConfInterface(this.generalConf);
		},
		generalConfRequiredFieldsIds() {
			return this.getConfigurationRequiredFieldIds(this.generalConfInterface);
		},
		isGeneralConfigurationValid() {
			return this.isConfValid(this.generalConfId, this.generalConfInterface, this.generalConfRequiredFieldsIds);
		},
		userConfId() {
			return this.configurationIds?.userSpecificConfigurationId;
		},
		userConf() {
			return this[getConfiguration.fullPath](this.userConfId);
		},
		userConfInterface() {
			return this.getConfInterface(this.userConf);
		},
		userConfRequiredFieldsIds() {
			return this.getConfigurationRequiredFieldIds(this.userConfInterface);
		},
		isUserConfigurationValid() {
			return this.isConfValid(this.userConfId, this.userConfInterface, this.userConfRequiredFieldsIds);
		},
		showErrorMessage() {
			return this.unValidConfigurationsIds.length > 0 && this.didUserTriedToSaveOnce === true;
		},
		areConfigurationsValid() {
			return this.unValidConfigurationsIds.length === 0;
		},
		isInstallationButtonDisabled() {
			if (this.isLastTab) {
				return !this.areConfigurationsValid || this.isSaving;
			}
			return this.isOpenedPanelNotValid || this.isSaving;
		},
		openedPanel() {
			return this.panels[this.openedPanelIndex];
		},
		isOpenedPanelNotValid() {
			return this.unValidConfigurationsIds.includes(Number(this.openedPanel?.configurationId));
		},

		generalConfigurationId() {
			if (this.configurationIds) {
				return this.configurationIds.generalConfigurationId;
			} else {
				return this[generalConfigurationId.fullPath];
			}
		},
		userSpecificConfigurationId() {
			if (this.configurationIds) {
				return this.configurationIds.userSpecificConfigurationId;
			} else {
				return this[userSpecificConfigurationId.fullPath];
			}
		},
		generalConfigurationPanel() {
			return {
				title: 'General setup',
				description: 'For everyone',
				ref: 'general_configuration',
				isGeneralConfiguration: true,
				widget: this.generalConfInterface,
				configurationId: this.generalConfigurationId,
				tooltipDescription: 'If you share this feature with your team, values below will be used for all users.',
			};
		},
		personalConfigurationPanel() {
			return {
				title: 'Personal setup',
				description: 'For you',
				ref: 'personal_configuration',
				widget: this.userConfInterface,
				configurationId: this.userSpecificConfigurationId,
				tooltipDescription: 'If you share this feature with your team, each user will enter their own values.',
			};
		},
		panels() {
			const allPanels = [this.generalConfigurationPanel, this.personalConfigurationPanel];
			if (this.editMode) {
				return allPanels;
			} else {
				return allPanels.filter((panel) => !!panel.configurationId && panel?.widget?.fields?.length > 0);
			}
		},
	},
	methods: {
		onInstallationAdvance() {
			if (this.isLastTab) {
				this.saveConfigurations();
				return;
			}
			this.openedPanelIndex = this.openedPanelIndex + 1;
		},
		...mapActions([saveConfigurationsResponses.fullPath, setConfigurationsValues.fullPath, setConfigurationValues.fullPath]),
		getConfInterface(conf) {
			return conf?.workflow_configuration_interfaces[0].interface;
		},
		getConfigurationRequiredFieldIds(confInterface) {
			return confInterface?.fields
				.filter((field) => field.metadata.options.required?.enabled || NONE_INTERFACE_CONFIGURATION_ONLY_FIELDS.includes(field.field_type))
				.map((field) => field.id);
		},
		isConfValid(confId, confInterface, confRequiredFieldIds) {
			const interfaceResponse = this.configurationsValues?.[confId]?.[confInterface?.id] || {};
			return confRequiredFieldIds?.every(
				(requiredFieldId) => interfaceResponse[requiredFieldId] && this.validateObjectResponse(interfaceResponse[requiredFieldId])
			);
		},
		addNewBlock(configurationType) {
			// Triggered from parent
			this.$refs.menu_content.addNewBlock(configurationType);
		},
		async saveConfigurations() {
			this.didUserTriedToSaveOnce = true;
			if (!this.areConfigurationsValid) {
				return;
			}
			this.isSaving = true;
			try {
				await this[saveConfigurationsResponses.fullPath]({
					response: this.configurationsValues,
					applyConfigurationsResponseOnDraft: this.applyConfigurationsResponseOnDraft,
				});
				this.$emit('close');
			} catch {
				this.$toast.error(`An error occurred, please try again later`);
			} finally {
				this.didUserTriedToSaveOnce = false;
				this.isSaving = false;
			}
		},
		validateObjectResponse(val) {
			if (isPlainObject(val)) {
				//todo - if we have more then one object type response we should know the difference here
				return DynamicDataService.isMappingResponseValid(val);
			}
			return true;
		},
		getConfigurationResponse(configurationResponseFromServer) {
			const interfaceId = this.getConfInterface(configurationResponseFromServer)?.id;
			return {
				[configurationResponseFromServer.id]: { [interfaceId]: { ...configurationResponseFromServer?.user_response?.fields_responses } },
			};
		},
		setConfigurationResponseInConfigurationsValues(configurationResponseFromServer) {
			if (configurationResponseFromServer) {
				this[setConfigurationValues.fullPath](this.getConfigurationResponse(configurationResponseFromServer));
			}
		},
	},
	data() {
		return {
			isSaving: false,
			openedPanelIndex: 0,
			didUserTriedToSaveOnce: false,
		};
	},
	created() {
		this[setConfigurationsValues.fullPath]({});
		this.setConfigurationResponseInConfigurationsValues(this.userConf);
		this.setConfigurationResponseInConfigurationsValues(this.generalConf);
	},
	watch: {
		userConf(newV) {
			this.setConfigurationResponseInConfigurationsValues(newV);
		},
		generalConf(newV) {
			this.setConfigurationResponseInConfigurationsValues(newV);
		},
	},
};
</script>

<style scoped lang="scss">
.menu-container {
	$menu-header-height: 30px;
	width: 520px;
	@media (max-height: 800px) {
		height: 430px;
	}
	@media (min-height: 800px) {
		height: 610px;
	}

	.menu-header {
		font-size: 12px;
		font-weight: 600;
		color: var(--bold-title);

		height: $menu-header-height;
		border-radius: 10px 10px 0px 0px;
		background: var(--setup-node-container-background);
	}

	.content-and-footer-container {
		background: var(--bg-color);
		border-radius: 0px 0px 10px 10px;
		height: calc(100% - #{$menu-header-height});

		.menu-content {
			overflow-y: auto;
		}

		.footer-container {
			min-height: 60px;
			max-height: 60px;
			border-radius: 0px 0px 8px 8px;
			border-top: 1px solid var(--border-color);
			box-shadow: 0px 3px 9px var(--shadow-color);

			.save-button {
				width: 90px;
				height: 35px;
				padding: 0px !important;
				font-size: 13px !important;
				border-radius: 5px !important;
				color: var(--purpleish) !important;
				background: var(--bg-color) !important;
				border: 1px solid var(--purpleish) !important;
			}

			.error-message {
				font-size: 12px;
				color: #fc337a;
			}
		}
	}
}
.installation-button {
	width: 166px;
	height: 42px;
	padding: 0px !important;
	font-size: 14px !important;
	border-radius: 8px !important;
	color: var(--bg-color) !important;
	background: var(--bold-border-color) !important;
}
</style>
